import { Link } from "react-router-dom";
import Figure from "./Figure";
import RoleTimeline from "./RoleTimeline";
import { roleBars } from "./figures";

/**
 * ============================================================================
 * Career Dashboard — the Home strip (D-095).
 * ============================================================================
 *
 * `/dashboard` shipped in D-094 reachable only from a navbar dropdown; every
 * other route on this site has a strip on Home, and TODO recorded the omission.
 * This is that strip.
 *
 * IT PRINTS NO NUMBER OF ITS OWN. Every figure goes through the same `<Figure>`
 * as the full route, which resolves against the registry `figures.ts` derives
 * from the content layer and throws on an id it cannot source — and the
 * pre-render executes this component, so a fabricated teaser figure fails the
 * build. That was the brief's explicit condition for these strips and it is the
 * same guarantee the destination page already carried, not a weaker version of
 * it for a summary.
 *
 * WHAT IT SHOWS, AND WHY NOT THE KPIs. `MetricsBand` already sets the eight
 * headline figures directly under the hero. Repeating them here would be the
 * same numbers twice on one page with a link in between, which reads as
 * padding and gives a reader no reason to follow it. So this strip shows what
 * the band does not: the SHAPE of the record — the career as a set of cards, and
 * the three counts the dashboard's other panels are built on.
 *
 * THE TIMELINE IS NO LONGER A REDUCTION OF THE FULL ONE — IT IS THE FULL ONE
 * (D-102, owner instruction: "make it identical"). It was a mini version at a
 * third of the height, and the two surfaces had drifted into showing different
 * FIELDS as well as different sizes: the page carried the earlier-title pill and
 * the role summary, this strip carried the location and the "Current" state. A
 * reader who saw both saw two accounts of one record.
 *
 * `RoleTimeline` is now that layout, once, rendered by both. The honest cost is
 * recorded here rather than glossed: this strip is no longer strictly a teaser
 * for the timeline, so the reason to follow the link is the OTHER panels —
 * skills, credentials, the build portfolio — and not more detail on the roles.
 * That is the owner's call and it is the shape they asked for. What the strip
 * still chooses for itself is the destination: `/dashboard`, the page it
 * introduces.
 *
 * IT NAMES THE EMPLOYMENT AS OF D-100, on the owner's instruction, and the
 * first version is worth recording because it looked finished. Each row was a
 * tenure ("1 yr 10 mo") and a coloured bar — so the strip drew four rectangles
 * a reader could not attach to a job. The record was on the page, in
 * `Experience_v2` above, which is what made the omission survive: the bar was
 * `aria-hidden` and defensible only because something ELSE stated the facts. A
 * panel that depends on a section above it for its meaning is a panel that
 * breaks silently the day it is moved or linked to. Title, employer, location
 * and the span now sit on the row itself.
 */
/**
 * A count card, and since D-097 a LINK to the section that owns the count —
 * owner ruling: "every card should be clickable and should redirect to the
 * individual page".
 *
 * The whole card is the link rather than a "view more" inside it. There is no
 * other interactive content in here, so a wrapping `<Link>` gives exactly one
 * tab stop and one entry in the accessibility tree; D-081 needed a stretched
 * overlay on the workshop cards only because those carry links of their own.
 *
 * `to` IS REQUIRED, NOT OPTIONAL. A count card whose destination could be
 * omitted is a card that silently stops being clickable when someone adds the
 * fourth one, which is the state the ruling exists to prevent — and TypeScript
 * refusing to compile is a better gate than a sweep noticing later.
 *
 * The accessible name is built from the label rather than left as the bare
 * number: "27" as a link text says nothing about where it goes.
 */
const TeaserCount = ({ id, label, to }: { id: string; label: string; to: string }): JSX.Element => (
	<div className="card-shell">
		<Link
			to={to}
			aria-label={`${label} — open the section`}
			className="card-face flex h-full flex-col p-5 no-underline focus:outline-none focus-visible:ring-4 focus-visible:ring-[var(--ring-accent)]"
		>
			<Figure id={id} className="text-3xl font-semibold leading-none text-[var(--text-strong)]" />
			<span className="mt-2 text-sm font-medium text-[var(--text-secondary)]">{label}</span>
		</Link>
	</div>
);

export default function DashboardTeaser(): JSX.Element | null {
	/* A section with nothing in it must not ship (Constitution II.4). It cannot
	   happen while the layer holds a single role, and it is one line. */
	if (!roleBars.length) return null;

	return (
		<section className="section-shell" id="dashboard" aria-labelledby="dashboard-teaser-heading">
			<div className="section-rail">
				<p className="section-eyebrow">The Record, Drawn</p>

				<h2 id="dashboard-teaser-heading" className="heading">
					Career{" "}
					<span className="bg-gradient-to-r from-[var(--grad-brand-from)] via-[var(--grad-brand-via)] to-[var(--grad-brand-to)] bg-clip-text text-transparent">
						Dashboard
					</span>
				</h2>

				<p className="section-standfirst">
					The same record set in prose above, drawn instead — every figure read from the content layer rather
					than typed.
				</p>

				{/* ONE COMPONENT, SHARED WITH `/dashboard` (D-102, owner instruction:
				    "make it identical"). The two surfaces drew this timeline differently
				    — the page showed the earlier-title pill and the summary, this strip
				    showed the location and the "Current" state — so a reader who saw
				    both saw two accounts of one record. They are now one layout, and the
				    only thing this surface chooses is the destination: `/dashboard`, the
				    page it is a teaser for. */}
				<div className="mt-10">
					<RoleTimeline to="/dashboard" />
					<p className="mt-4 text-xs text-[var(--text-muted)]">
						<Figure id="roles:total" className="font-semibold text-[var(--text-secondary)]" /> roles, newest
						at the top, each bar drawn to its own length.
					</p>
				</div>

				{/* --------------------------------------------------------- counts -- */}
				<div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-3">
					{/* Each destination is the section that OWNS the count, so the
					    number a reader clicks is the number they land among:
					    credentials in the Certifications section, skills in Skills,
					    builds on /projects. Two of the three are same-page anchors
					    because this strip is on Home. */}
					<TeaserCount id="certs:total" label="Credentials published" to="/#certifications" />
					<TeaserCount id="skills:total" label="Named skills" to="/#skills" />
					<TeaserCount id="projects:total" label="Builds delivered" to="/projects" />
				</div>

				{/* THESE TWO WENT THROUGH `<Figure>` ON THE SECOND PASS, and the
				    first pass is worth recording: they were written as
				    `{certCategoryBars.length}` and `{skillBars.length}` —
				    interpolated straight into the markup, correct values, and
				    outside the registry. Correct is not the property being
				    defended. A number that reaches a page without an id carries no
				    `data-figure`, so the sweep cannot join it to anything, and the
				    guarantee this whole mechanism exists to make ("no figure on
				    this site is unsourced") silently stops covering it. */}
				<p className="mt-4 text-xs text-[var(--text-muted)]">
					Grouped on the full view into{" "}
					<Figure id="certs:categories" className="font-semibold text-[var(--text-secondary)]" /> credential
					categories and{" "}
					<Figure id="skills:categories" className="font-semibold text-[var(--text-secondary)]" /> skill
					disciplines, beside the delivery portfolio and the headline figures.
				</p>

				<div className="mt-8">
					<Link
						to="/dashboard"
						className="inline-block min-h-[24px] text-sm font-medium text-[var(--accent-link)] transition-colors duration-200 hover:text-[var(--accent-link-hover)]"
					>
						Open the full dashboard &rarr;
					</Link>
				</div>
			</div>
		</section>
	);
}
